import { useEffect, useRef, useState } from "react";
import { FileUp, Loader2, X } from "lucide-react";
import { api } from "@/lib/api";

const OK_EXT = ["pdf", "jpg", "jpeg", "png", "dxf"];
export const MAX_FILES = 5;
export const TOO_MANY = "You can upload up to 5 files at a time";
export const DRAWING_ERR = "Unsupported file. Please upload PDF, JPG, PNG or DXF (export DWG to PDF or DXF)";
export const DWG_ERR = "DWG not supported. Please export to PDF or DXF.";
const SIZE_ERR = "File is too large. Maximum size is 100 MB";
const MB = 1024 * 1024;
export const BATCH_ERR = "These files add up to more than 250 MB. Please upload them in two batches.";

const check = (f) => {
  const ext = f.name.includes(".") ? f.name.split(".").pop().toLowerCase() : "";
  if (ext === "dwg") return DWG_ERR;
  if (!OK_EXT.includes(ext)) return DRAWING_ERR;
  return f.size > 100 * MB ? SIZE_ERR : "";
};
const limitErr = (list) => {
  const ok = list.filter((p) => !p.error);
  if (ok.length > MAX_FILES) return TOO_MANY;
  return ok.reduce((s, p) => s + p.file.size, 0) > 250 * MB ? BATCH_ERR : "";
};
const size = (b) => (b > 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);
const cr = (n) => `${n} credit${n === 1 ? "" : "s"}`;

export function useReadingPrices() {
  const [p, setP] = useState({ schedule: 1, plans: 2 });
  useEffect(() => { api.get("/reading-prices").then((r) => setP(r.data)).catch(() => {}); }, []);
  return p;
}

export function FilePicker({ onStart, busy, title, startLabel = "Read files", compact = false, progress = null }) {
  const prices = useReadingPrices();
  const input = useRef(null);
  const [drag, setDrag] = useState(false);
  const [picked, setPicked] = useState([]);
  const [error, setError] = useState("");
  const good = picked.filter((p) => !p.error);
  const add = (list) => {
    const next = [...picked, ...Array.from(list || []).map((file) => ({ key: `${file.name}-${file.size}-${Math.random()}`, file, error: check(file) }))];
    setError(limitErr(next));
    setPicked(next);
  };
  const remove = (key) => {
    const next = picked.filter((p) => p.key !== key);
    setPicked(next);
    setError(limitErr(next));
  };
  const start = () => {
    const err = limitErr(picked);
    if (err) return setError(err);
    if (good.length) onStart(good.map((p) => p.file));
  };
  return (
    <div className="space-y-4">
      <div data-testid="upload-dropzone" onClick={() => !busy && input.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); add(e.dataTransfer.files); }}
        className={`group relative flex cursor-pointer flex-col items-center justify-center overflow-hidden rounded-2xl border-2 border-dashed px-8 text-center transition-colors duration-200 ${compact ? "min-h-[160px] py-8" : "min-h-[260px] py-12"} ${drag ? "border-amber-400 bg-amber-500/5" : "border-slate-700/80 bg-[#F2F2F7]/60 hover:border-amber-500/60 hover:bg-[#F2F2F7]"}`}>
        <input ref={input} type="file" multiple accept=".pdf,.jpg,.jpeg,.png,.dxf" className="hidden" data-testid="upload-dropzone-input"
          onChange={(e) => { add(e.target.files); e.target.value = ""; }} />
        <div className="mb-5 grid h-14 w-14 place-items-center rounded-2xl border border-slate-700 bg-slate-900 transition-transform duration-300 group-hover:-translate-y-1">
          {busy ? <Loader2 className="h-6 w-6 animate-spin text-amber-400" /> : <FileUp className="h-6 w-6 text-amber-400" />}
        </div>
        <p className="font-display text-xl font-bold text-slate-100">{title}</p>
        <p className="mt-2 text-sm text-slate-400">Up to 5 files: schedules and plans together. PDF, JPG, PNG or DXF, up to 100 MB and 150 pages each (250 MB per upload).</p>
        <p data-testid="upload-credit-note" className="mt-3 text-xs text-slate-500">
          <span data-testid="upload-credit-prices" className="font-semibold text-slate-300">Schedules: {cr(prices.schedule)} · Plans only: {cr(prices.plans)}</span>
          {" "}per upload · refunded automatically if nothing is found or every file fails
        </p>
      </div>
      {picked.length > 0 && (
        <ul data-testid="picked-files-list" className="divide-y divide-slate-800 overflow-hidden rounded-xl border border-slate-800">
          {picked.map((p, i) => (
            <li key={p.key} data-testid={`picked-file-${i}`} className="flex items-center gap-3 px-4 py-2.5">
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-slate-100">{p.file.name}</p>
                {p.error ? <p data-testid={`picked-file-error-${i}`} className="text-xs text-red-400">Rejected · {p.error}</p>
                  : <p className="text-xs text-slate-500">{size(p.file.size)}</p>}
              </div>
              <button data-testid={`picked-file-remove-${i}`} onClick={() => remove(p.key)} disabled={busy} title="Remove"
                className="rounded-md p-1.5 text-slate-500 transition-colors hover:bg-red-500/10 hover:text-red-400"><X className="h-4 w-4" /></button>
            </li>
          ))}
        </ul>
      )}
      {error && <p data-testid="upload-error" className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">{error}</p>}
      {busy && progress !== null && (
        <div data-testid="upload-progress" className="space-y-1.5">
          <div className="flex justify-between text-xs text-slate-400"><span>Uploading…</span><span data-testid="upload-progress-pct">{progress}%</span></div>
          <div className="h-2 overflow-hidden rounded-full bg-slate-800"><div className="h-full rounded-full bg-amber-500 transition-[width] duration-300" style={{ width: `${progress}%` }} /></div>
        </div>
      )}
      {picked.length > 0 && (
        <button data-testid="start-upload-button" onClick={start} disabled={busy || !good.length || !!limitErr(picked)}
          className="inline-flex items-center gap-2 rounded-full bg-amber-500 px-6 py-2.5 text-sm font-semibold text-slate-950 transition-colors hover:bg-amber-400 disabled:opacity-50">
          {busy && <Loader2 className="h-4 w-4 animate-spin" />} {startLabel} ({good.length} file{good.length === 1 ? "" : "s"})
        </button>
      )}
    </div>
  );
}
